import i18next from 'i18next';
import moment from 'moment-timezone';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import utilsXLS, { generateFiles } from '@commons/utils/makeXLS';

import { ENUM_STATUS, OK_STATUSES } from './constants';

const getUniquePosNames = (filteredRows) => {
  const posNames = [];
  const seen = new Set();

  filteredRows.forEach(({ store }) => {
    (store?.cashierStoresMapping || []).forEach(({ posName }) => {
      if (!seen.has(posName)) {
        seen.add(posName);
        posNames.push(posName);
      }
    });
  });

  return posNames;
};

const getPosValue = (turnoverByPos, posName, status) => {
  const pos = (turnoverByPos || []).find((entry) => entry.posName === posName);

  if (!pos) {
    return '-';
  }

  if (status === ENUM_STATUS.CLOSED) {
    return 0;
  }

  if (!OK_STATUSES.includes(status)) {
    return '-';
  }

  return pos.hasReported === false ? '-' : pos.turnover;
};

const getTotalTurnoverValue = (turnover, status) => {
  if (status === ENUM_STATUS.CLOSED) {
    return 0;
  }

  return OK_STATUSES.includes(status) && turnover != null ? turnover : '-';
};

const getPosStatusValue = (turnoverByPos, posName, status) => {
  const pos = (turnoverByPos || []).find((entry) => entry.posName === posName);

  if (!pos) {
    return '-';
  }

  if (status === ENUM_STATUS.CLOSED) {
    return i18next.t('ADMIN.FTP_DEBUG.STATUS_CLOSED');
  }

  if (!OK_STATUSES.includes(status)) {
    return '-';
  }

  return pos.hasReported === false
    ? i18next.t('ADMIN.FTP_DEBUG.STATUS_POS_NOT_REPORTED')
    : i18next.t('ADMIN.FTP_DEBUG.STATUS_OK');
};

const buildDataRows = (filteredRows, selectedDates, posNames, hasMultiplePos) =>
  filteredRows.flatMap(({ storeName, store, ...cellsByDate }) =>
    selectedDates.map((date) => {
      const { status, turnover, turnoverByPos } = cellsByDate[date] || {};

      const posValues = {};
      const posStatusValues = {};

      posNames.forEach((posName) => {
        posValues[posName] = getPosValue(turnoverByPos, posName, status);

        if (hasMultiplePos) {
          posStatusValues[posName] = getPosStatusValue(turnoverByPos, posName, status);
        }
      });

      return {
        storeName,
        date,
        statusLabel: status ? i18next.t(`ADMIN.FTP_DEBUG.STATUS_${status.toUpperCase()}`) : '-',
        posValues,
        posStatusValues,
        totalTurnover: getTotalTurnoverValue(turnover, status),
      };
    }),
  );

const FIXED_COLUMNS_BEFORE_POS = 3;

const getDataColumns = (posNames, hasMultiplePos) => [
  { name: i18next.t('GENERAL.STORE'), propertyKey: 'storeName' },
  {
    name: i18next.t('GENERAL.DATE'),
    propertyKey: 'date',
    transform: (value) => moment(value).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
  },
  {
    name: hasMultiplePos
      ? i18next.t('ADMIN.FTP_DEBUG.EXPORT_COLUMN_GENERAL_STATUS')
      : i18next.t('GENERAL.STATUS'),
    propertyKey: 'statusLabel',
  },
  ...posNames.flatMap((posName) => [
    {
      name: posName,
      propertyKey: 'posValues',
      transform: (posValues) => (posValues ? posValues[posName] : '-'),
    },
    ...(hasMultiplePos
      ? [
          {
            name: i18next.t('ADMIN.FTP_DEBUG.EXPORT_COLUMN_POS_STATUS', { posName }),
            propertyKey: 'posStatusValues',
            transform: (posStatusValues) => (posStatusValues ? posStatusValues[posName] : '-'),
          },
        ]
      : []),
  ]),
  {
    name: i18next.t('ADMIN.FTP_DEBUG.EXPORT_COLUMN_TOTAL_TURNOVER'),
    propertyKey: 'totalTurnover',
  },
  { name: i18next.t('GENERAL.CURRENCY'), propertyKey: 'currencyCode' },
];

const getInformationsColumns = () => [
  { name: i18next.t('ADMIN.FTP_DEBUG.EXPORT_INFO_COLUMN_DATA'), propertyKey: 'dataLabel' },
  {
    name: i18next.t('ADMIN.FTP_DEBUG.EXPORT_INFO_COLUMN_ESTABLISHMENTS'),
    propertyKey: 'storeName',
  },
  {
    name: i18next.t('ADMIN.FTP_DEBUG.EXPORT_INFO_COLUMN_START_DATE'),
    propertyKey: 'startDate',
    transform: (value) => moment(value).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
  },
  {
    name: i18next.t('ADMIN.FTP_DEBUG.EXPORT_INFO_COLUMN_END_DATE'),
    propertyKey: 'endDate',
    transform: (value) => moment(value).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
  },
  {
    name: i18next.t('ADMIN.FTP_DEBUG.EXPORT_INFO_COLUMN_EXPORT_DATE'),
    propertyKey: 'exportDate',
    transform: (value) => moment(value).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
  },
];

const buildInformationsRows = (exportedStores, startDate, endDate, exportDate) =>
  exportedStores.map(({ name }) => ({
    dataLabel: i18next.t('GENERAL.METRIC_TURNOVER_EX_TAX_WITHOUT_CURRENCY'),
    storeName: name,
    startDate,
    endDate,
    exportDate,
  }));

export const exportFtpDebugList = ({
  filteredRows,
  selectedDates,
  currency,
  startDate,
  endDate,
  timezone,
}) => {
  const sortedRows = [...filteredRows].sort((rowA, rowB) =>
    rowA.storeName.localeCompare(rowB.storeName),
  );

  const posNames = getUniquePosNames(sortedRows);
  const hasMultiplePos = posNames.length > 1;

  const dataRows = buildDataRows(sortedRows, selectedDates, posNames, hasMultiplePos).map(
    (row) => ({
      ...row,
      currencyCode: currency.name,
    }),
  );

  const posColumnsCount = hasMultiplePos ? posNames.length * 2 : posNames.length;

  const dataSheet = {
    ...utilsXLS.generateDefaultSheet(
      i18next.t('FEATURE.ADMIN.FTP_DEBUG'),
      getDataColumns(posNames, hasMultiplePos),
      dataRows,
    ),
    formatToNumberColumnIndexes: {
      start: FIXED_COLUMNS_BEFORE_POS,
      end: FIXED_COLUMNS_BEFORE_POS + posColumnsCount,
    },
  };

  const exportDate = moment.tz(timezone);

  const exportedStores = sortedRows.map(({ store }) => store);

  const informationsSheet = utilsXLS.generateDefaultSheet(
    i18next.t('ADMIN.FTP_DEBUG.EXPORT_INFO_SHEET_NAME'),
    getInformationsColumns(),
    buildInformationsRows(exportedStores, startDate, endDate, exportDate),
  );

  const fileNameDate = exportDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);

  generateFiles(
    i18next.t('ADMIN.FTP_DEBUG.EXPORT_FILE_NAME', { date: fileNameDate }),
    [dataSheet],
    informationsSheet,
  );
};
